import { useEffect, useState } from 'react';
import { AVATAR_TYPES, type Profile } from '@forecastify/shared';
import { Avatar, Button, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Card, CardColumn, Field } from '../card';
import { useSession } from '../../session';
import { forgetAvatars, useAvatar } from '../use-avatar';
import { DropZone } from '../drop-zone';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Profile — and the two fields the mockup forgot to draw.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE JOB TITLE IS GONE — item 7.** The mockup had a `usSetTitle` in its logic and no input
 * anywhere, so C6 built the field from the setter; the 9 Sep 2026 round asked for it back out,
 * `0101` dropped `users.title`, and nothing reads it now.
 *
 * ⚠️ **In its place, the two halves of a name — item 6.** The form asks for them separately because
 * the ORDER is the reader's, not the writer's: `personName()` draws `Kovács Anna` in Hungarian and
 * `Anna Kovács` in English out of the same two fields. `displayName` stays and stays required —
 * it is the canonical, family-first form the trigger composes, what sorts and what a row we could
 * not split still renders.
 *
 * ⚠️ **The email change has five undrawn keys**, built here because `chunks.md` says to
 * (`docs/plan/mockup-findings.md` lists all 24).
 *
 * ⚠️ **Email is NOT part of the profile form**, even though it looks like it belongs. Changing an
 * address is a GoTrue flow with a confirmation mail and a pending state; putting it in a form that
 * also renames you means a partial success the screen cannot describe — "your name saved but your
 * address is pending" from one button. So it is its own card with its own button.
 */

/**
 * ⚠️ **Two initials, from the first and last word.** `Avatar` deliberately knows nothing about
 * people, so deriving them is the screen's job — and "Takács Gábor" must give `TG`, not `TA`.
 */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function ProfileSection({
  profile,
  onSave,
  onChangeEmail,
  onDirtyChange,
  onReload,
}: {
  readonly profile: Profile;
  readonly onSave?: (patch: {
    /* ⚠️ Optional since the form stopped asking — `0101`’s trigger composes it. */
    displayName?: string;
    /* ⚠️ Omitted rather than nulled when blank: the contract's halves are `.optional()`, and an
       absent key means "leave it" — there is no "clear one half" on the wire, because half a
       split name is not a state `personName()` can draw. */
    givenName?: string;
    familyName?: string;
    phone: string | null;
    version: number;
  }) => Promise<void>;
  readonly onChangeEmail?: (email: string) => Promise<void>;
  readonly onDirtyChange?: (dirty: boolean) => void;
  /** C22 · re-reads the account after a picture changes, so `avatarUrl` comes from the
   * server rather than being invented here. */
  readonly onReload?: () => Promise<void>;
}) {
  const t = useT();

  /**
   * ⚠️ **The form holds its own draft strings.** Deriving an input's value from the loaded profile
   * makes an in-progress edit unrepresentable — the same defect as a tag editor that renders
   * `join(', ')` and parses `split(',')`, where a comma becomes impossible to type and every test
   * passes because they all set values programmatically.
   */
  const [givenName, setGivenName] = useState(profile.givenName ?? '');
  const [familyName, setFamilyName] = useState(profile.familyName ?? '');
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [saving, setSaving] = useState(false);

  const [changingEmail, setChangingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  /* ⚠️ Compared against the LOADED profile, not tracked with a boolean. A flag leaves the Save
   * button offering to write the state that is already stored. */
  const dirty =
    givenName !== (profile.givenName ?? '') ||
    familyName !== (profile.familyName ?? '') ||
    phone !== (profile.phone ?? '');

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  /* ⚠️ An empty input means "remove this", which is `null` on the wire — `undefined` would mean
   * "leave it alone", and the two are deliberately different in the contract. */
  const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

  /* ⚠️⚠️ **BOTH HALVES, BECAUSE THE HALVES ARE THE NAME NOW.** `0101`'s trigger only composes
   * `display_name` when it has both, so saving one half alone would leave the canonical name as
   * whatever it was and the screen showing something else. A name that could not be split — the
   * one case the halves are null for — is fixed by typing both, which is the same act. */
  const nameEmpty = familyName.trim() === '' || givenName.trim() === '';

  /* ── C22 · the profile picture ─────────────────────────────────────────────────────────────── */
  const session = useSession();
  const avatar = useAvatar(profile.avatarUrl);
  const [busy, setBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const uploadAvatar = async (file: File): Promise<void> => {
    setBusy(true);
    setAvatarError(null);

    const form = new FormData();
    form.set('file', file);
    const response = await session
      .request('/files?target=user_avatar&targetId=me', { method: 'POST', body: form })
      .catch(() => null);
    setBusy(false);

    if (response?.ok === true) {
      /* ⚠⚠️ **THE PATH DOES NOT CHANGE WHEN THE PICTURE DOES** — `/files/avatar/<user>` is
       * derived from the user id, not from the content — so every cached copy of the old face
       * (the sidebar chip, a list, a board card) has to be dropped by hand. */
      forgetAvatars();
      /* ⚠️ And the SESSION is re-read, because `avatarUrl` there is what tells the sidebar chip
       * there is a picture at all — it was null for somebody uploading their first one. */
      await session.reload();
      /* ⚠️ The whole account is reloaded rather than the picture patched in: `avatarUrl` is the
       * server's answer, and a client that invents it is a second place that knows the path. */
      await onReload?.();
      return;
    }

    const body = (await response?.json().catch(() => null)) as
      { error?: { code?: string } } | null | undefined;
    const code = body?.error?.code;
    setAvatarError(
      code === 'file_too_large'
        ? t('account.avatar.tooLarge')
        : code === 'unsupported_file_type'
          ? t('account.avatar.wrongType')
          : code === 'file_infected'
            ? t('account.avatar.infected')
            : code === 'scanner_unavailable'
              ? t('account.avatar.scannerDown')
              : t('account.avatar.failed'),
    );
  };

  const removeAvatar = async (): Promise<void> => {
    await session.request('/files/avatar', { method: 'DELETE' }).catch(() => null);
    forgetAvatars();
    await session.reload();
    await onReload?.();
  };

  return (
    <CardColumn>
      <Card title={t('account.avatar.title')}>
        <div className="flex items-center gap-4">
          {/* ⚠️ **C22 wired this up.** The note that used to sit here said *"there is nowhere to put
              an uploaded file yet: object storage is not built, which is why the upload button
              below is disabled"* — R-20 built it, so the button works and the picture renders.
              The initials stay behind the image, which is what a failed load falls back to. */}
          <Avatar
            fallback={initials(profile.displayName)}
            seed={profile.avatarSeed ?? undefined}
            title={profile.displayName}
            size={64}
            src={avatar}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {/* ⚠️ Item 5: an AREA, not a button. `drop-zone.tsx` argues it. */}
            <DropZone
              accept={AVATAR_TYPES}
              busy={busy}
              onFiles={(files) => {
                const file = files[0];
                if (file !== undefined) void uploadAvatar(file);
              }}
            />
            {profile.avatarUrl === null ? null : (
              <Button
                variant="ghost"
                size="sm"
                className="self-start"
                onClick={() => void removeAvatar()}
              >
                {t('account.avatar.remove')}
              </Button>
            )}
            <p className="text-muted-foreground text-[11.5px]">{t('account.avatar.rules')}</p>
            {avatarError === null ? null : (
              <p className="text-destructive text-[11.5px]">{avatarError}</p>
            )}
          </div>
        </div>
      </Card>

      <Card
        title={t('account.basics.title')}
        actions={
          dirty ? (
            <>
              <span className="text-muted-foreground text-[11.5px]">
                {t('account.section.unsaved')}
              </span>
              <Button
                size="sm"
                disabled={saving || nameEmpty}
                onClick={() => {
                  if (onSave === undefined) return;
                  setSaving(true);
                  const given = givenName.trim();
                  const family = familyName.trim();
                  void onSave({
                    /* ⚠️ No `displayName`: the trigger composes it from the two halves, and
                       sending a third answer would be offering to set what is about to be
                       overwritten. */
                    ...(given === '' ? {} : { givenName: given }),
                    ...(family === '' ? {} : { familyName: family }),
                    phone: orNull(phone),
                    version: profile.version,
                  }).finally(() => {
                    setSaving(false);
                  });
                }}
              >
                {saving ? t('account.saving') : t('account.save')}
              </Button>
            </>
          ) : undefined
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {/* ⚠️⚠️ **THE FULL-NAME BOX IS GONE — ITEM 6, SECOND ASKING.** That item split the name in
              two and left a *Name* field above the halves, so the form asked for the same name
              twice and only one of the two answers survived: `0101`'s trigger composes
              `display_name` from the halves and overwrites whatever was typed here. Reported as
              *"why do people still have a separate full name field? Also pointless, I told you to
              get rid of it."*
              ⚠️⚠️ **AND THE NOTE UNDER THEM IS GONE TOO.** *"A sorrendet az olvasó nyelve dönti
              el"* explained a rule nobody had asked about, under two fields whose labels already
              say which is which — struck 20 Sep with the rest of the narration. */}
          <Field
            label={t('account.field.familyName')}
            error={nameEmpty ? t('error.validation_failed') : undefined}
          >
            <TextInput
              value={familyName}
              onChange={(event) => {
                setFamilyName(event.target.value);
              }}
            />
          </Field>

          <Field label={t('account.field.givenName')}>
            <TextInput
              value={givenName}
              onChange={(event) => {
                setGivenName(event.target.value);
              }}
            />
          </Field>

          <Field label={t('account.field.phone')}>
            {/* Mono, because a phone number is a figure somebody reads back digit by digit. */}
            <TextInput
              value={phone}
              onChange={(event) => {
                setPhone(event.target.value);
              }}
            />
          </Field>
        </div>
      </Card>

      <Card
        title={t('account.field.email')}
        hint={pendingEmail === null ? undefined : t('account.email.bothMustConfirm')}
        actions={
          changingEmail ? undefined : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setChangingEmail(true);
              }}
            >
              {t('account.email.change')}
            </Button>
          )
        }
      >
        <p className="text-[12.5px] break-all">{profile.email}</p>

        {pendingEmail !== null && (
          /* ⚠️ **The pending address, shown as pending.** GoTrue mails both addresses and the
             identity still carries the old one — a screen that swapped the displayed address on
             submit would be lying until two links are clicked, and the person would stop looking
             for the mail. */
          <p className="text-muted-foreground mt-2 text-[12px]">
            {t('account.email.pending', { email: pendingEmail })}
          </p>
        )}

        {changingEmail && (
          <div className="mt-3 flex flex-col gap-2">
            <Field label={t('account.email.newLabel')}>
              <TextInput
                type="email"
                value={newEmail}
                onChange={(event) => {
                  setNewEmail(event.target.value);
                }}
              />
            </Field>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={newEmail.trim() === ''}
                onClick={() => {
                  if (onChangeEmail === undefined) return;
                  const target = newEmail.trim();
                  void onChangeEmail(target).then(() => {
                    setPendingEmail(target);
                    setChangingEmail(false);
                    setNewEmail('');
                  });
                }}
              >
                {t('account.email.send')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setChangingEmail(false);
                  setNewEmail('');
                }}
              >
                {t('account.email.cancel')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </CardColumn>
  );
}
